import React, { useMemo, useState } from 'react';
import { Chip, Input } from '@heroui/react';
import { Typography } from '../../../../components';
import { SearchInputIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
} from '../../../../components/icons';

interface ProhibitedSourcesTooltipProps {
  sources: string[];
  offerShortId: string;
  modelUsername: string;
  profileUrl: string;
}

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  onlyfans: <OnlyFansIc size={16} fill="#A8A8B0" />,
  only_fans: <OnlyFansIc size={16} fill="#A8A8B0" />,
  instagram: <InstagramIc size={16} fill="#A8A8B0" />,
  google: <GoogleIc size={16} fill="#A8A8B0" />,
  twitter: <TwitterIc size={16} fill="#A8A8B0" />,
  reddit: <IcReddit size={16} />,
  telegram: <IcTg size={16} />,
  tiktok: <IcTikTok size={16} />,
  youtube: <IcYoutube size={16} />,
  other: <IcAnotherSoc size={16} />,
};

const SOURCE_LABELS: Record<string, string> = {
  onlyfans: 'OnlyFans',
  only_fans: 'OnlyFans',
  instagram: 'Instagram',
  google: 'Google',
  twitter: 'Twitter',
  reddit: 'Reddit',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  other: 'Other',
};

const ProhibitedSourcesTooltip: React.FC<ProhibitedSourcesTooltipProps> = ({
  sources,
  offerShortId,
  modelUsername,
  profileUrl,
}) => {
  const { t } = useTranslations();
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedSources = useMemo(() => {
    return sources
      .map((source) => {
        const key = source.toLowerCase();
        return {
          key,
          label: SOURCE_LABELS[key] || source.charAt(0).toUpperCase() + source.slice(1),
          icon: SOURCE_ICONS[key] || <GlobalIc size={16} />,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sources]);

  const filteredSources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedSources;

    return normalizedSources.filter(
      (source) =>
        source.label.toLowerCase().includes(query) || source.key.toLowerCase().includes(query)
    );
  }, [normalizedSources, searchQuery]);

  return (
    <div className="flex w-[25rem] max-w-[25rem] flex-col gap-4 p-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography variant="h6" className="text-auth-ink text-base leading-7 font-medium">
              {t('revshare.prohibitedSources.title') || 'Запрещённые источники'}
            </Typography>
            <Chip
              size="sm"
              radius="lg"
              classNames={{
                base: 'bg-auth-surface',
                content: 'text-sm font-medium !text-auth-muted',
              }}
            >
              {sources.length}
            </Chip>
          </div>
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-auth-ink font-medium">
              {offerShortId}
            </Typography>
            <a
              href={profileUrl}
              target="__blank"
              rel="noopener noreferrer"
              className="text-partner text-sm font-medium hover:underline"
            >
              @{modelUsername}
            </a>
          </div>
        </div>
      </div>

      {sources.length > 5 && (
        <div className="flex flex-col gap-2">
          <Input
            type="text"
            placeholder={t('revshare.prohibitedSources.search') || 'Поиск источника...'}
            size="sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            endContent={<SearchInputIc className="text-neutral-500" />}
            classNames={{
              base: 'w-full',
              input: 'text-sm text-auth-ink placeholder:text-neutral-500',
              inputWrapper: 'rounded-xl h-9 bg-auth-surface border border-transparent',
            }}
          />
        </div>
      )}

      <div className="flex max-h-[15rem] flex-col gap-3 overflow-y-auto pr-1 custom-scrollbar">
        {filteredSources.map((source) => (
          <div key={source.key} className="flex items-center gap-3 h-6 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center">{source.icon}</div>
            <Typography variant="body2" className="text-auth-ink">
              {source.label}
            </Typography>
          </div>
        ))}

        {filteredSources.length === 0 && (
          <div className="rounded-lg border border-tin px-3 py-4 text-center">
            <Typography variant="body2" className="text-auth-muted">
              {t('revshare.prohibitedSources.noResults') || 'Источники не найдены'}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProhibitedSourcesTooltip;
